import React, { useEffect, useState } from 'react';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import { IDefaultSmsAutoReplyModelEdit, ISmsAutoReplyDefaultReplyFormValues } from 'types/ISmsAutoReplies';
import { Form, Modal } from 'react-bootstrap';
import { Checkbox } from 'components/FormControls';
import Textarea from 'components/Inputs/Textarea';
import * as InboxApi from 'api/InboxAPI';
import { useSelector } from 'react-redux';
import { getCurrentAccount } from 'reducers/UserInfo';
import useSWR from 'swr';
import { ContentLength } from 'components/EventTemplateForm/ContentLength';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { toast } from 'react-toastify';

const ACTIVE_CHECKBOX_TOOLTIP = 'Inactive default reply will not send back responses.';
const RESPONSE_TOOLTIP = 'The content of the message sent to recipients when an incoming sms message is received, that does not match any of your other configured Auto-reply keywords and criteria.';

interface SmsAutoReplyDefaultReplyModalProps {
  onHide: () => void
  show: boolean
}

export const SmsAutoReplyDefaultReplyModal: React.FC<SmsAutoReplyDefaultReplyModalProps> = ({
  onHide, show,
}) => {
  const currentAccount = useSelector(getCurrentAccount);
  const currentAccountId = currentAccount?.Id;
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

  const methods = useForm<ISmsAutoReplyDefaultReplyFormValues>({
    mode: 'onChange',
    defaultValues: {
      isActive: false,
      response: '',
    },
  });

  const {
    reset,
    control,
    formState,
    errors,
    handleSubmit,
  } = methods;

  const { data: defaultReply, mutate } = useSWR(
    ['defaultsmsautoreply', currentAccountId],
    () => InboxApi.getDefaultSmsAutoReply(),
    {
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
    },
  );

  useEffect(() => {
    if (defaultReply) {
      reset({
        isActive: defaultReply?.IsActive ?? false,
        response: defaultReply?.Response ?? '',
      });
    }
  }, [defaultReply, reset]);

  const {
    isValid,
    isDirty,
  } = formState;

  const onSubmit = async (data: ISmsAutoReplyDefaultReplyFormValues) => {
    const submitData: IDefaultSmsAutoReplyModelEdit = {
      IsActive: data.isActive,
      Response: data.response.trim(),
    };

    try {
      setIsSubmitting(true);
      await InboxApi.setDefaultSmsAutoReply(submitData);
      toast.success('Account default sms Auto-reply is successfully configured.');
      mutate();
      onHide();
    } catch (err) {
      toast.error(err.response?.data?.Message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleHide = () => {
    onHide();
  }

  return (
    <Modal
      title="Set Default SMS Auto-Reply"
      onShow={() => {
        methods.reset();
      }}
      show={show}
      onHide={handleHide}
      className="w-[552px] flex flex-col"
    >
      <Modal.Header
        closeButton
        style={{ marginBottom: 12 }}
      >
        <Modal.Title>
          Set Default SMS Auto-Reply
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <FormProvider {...methods}>
          <Form
            className=""
            onSubmit={handleSubmit(onSubmit)}
          >
            <div className="p-6 overflow-y-auto min-h-0">
              <p>
                Configure your default SMS Auto-Reply response. This response will be sent back
                to a recipient whenever an incoming SMS message is received, that does not
                match any of your other configured Auto-reply keywords and criteria.
              </p>

              <div className="mt-6">
                <Checkbox
                  name="isActive"
                  id="isActive"
                  className="mr-3.5 mb-3 sms-auto-reply-text"
                  label="Is Active"
                  control={control}
                  customIcon={(
                    <FontAwesomeIcon icon="question-circle" size="1x" />
                  )}
                  customIconTooltip={ACTIVE_CHECKBOX_TOOLTIP}
                />
              </div>
              <div className="my-2">
                <Controller
                  errors={errors?.response && 'Response is required'}
                  rules={{
                    required: 'Content Required',
                    validate: (val: any) => {
                      if (val === null || val === undefined || val.trim() === '') {
                        return 'Content Required';
                      }
                      const regex = /[\uD800-\uDBFF][\uDC00-\uDFFF]/g;
                      if (regex.test(val)) {
                        return 'Response can not include emojis';
                      }
                      return true;
                    },
                  }}
                  control={control}
                  name="response"
                  render={({ ref, ...props }) => (
                    <>
                      <ContentLength name="response" maxLength={300} />
                      <Textarea
                        {...props}
                        id="Response"
                        label="Response"
                        maxLength={300}
                        rows={5}
                        errors={errors?.response?.message}
                        customIcon={(
                          <FontAwesomeIcon icon="question-circle" size="1x" />
                        )}
                        customIconTooltip={RESPONSE_TOOLTIP}
                      />
                    </>
                  )}
                />
              </div>
            </div>
          </Form>
        </FormProvider>
      </Modal.Body>
      <Modal.Footer>
        <button
          type="button"
          className="btn btn-secondary"
          onClick={handleHide}
        >
          Cancel
        </button>
        <button
          type="submit"
          className="ml-2 btn btn-primary"
          disabled={isSubmitting || !isValid || !isDirty}
          onClick={handleSubmit(onSubmit)}
        >
          Save
        </button>
      </Modal.Footer>
    </Modal>
  );
};
